import type { JSX } from "react";
import { useTranslation } from "react-i18next";

import Box from "@mui/material/Box";
import {
  SUBCONFIGURATION_NAVIGATION_DEFAULT_PROPS as DEFAULTS,
  SubconfigurationNavigationApiProvider,
  SubconfigurationNavigationContent,
  SubconfigurationNavigationProps,
  useSubconfigurationNavigation,
  useSubconfigurationNavigationItems,
} from "@plansoft-configuration-renderer/components";

import {
  CustomSubconfigurationNextButton,
  CustomSubconfigurationPreviousButton,
  CustomSubconfigurationResetButton,
} from "./CustomSubconfigurationButton";
import { CustomSubconfigurationNavigationComponents } from "./CustomSubconfigurationNavigationComponents";

/**
 * Tab control on configuration level
 * @category UI
 * @param props
 * @returns
 */

export function CustomSubconfigurationNavigation(
  props: SubconfigurationNavigationProps,
): JSX.Element {
  const {
    control = DEFAULTS.control,
    orientation = DEFAULTS.orientation,
    textMode = DEFAULTS.textMode,
    enableStickyNavigation = DEFAULTS.enableStickyNavigation,
    components = CustomSubconfigurationNavigationComponents,
  } = props;

  const items = useSubconfigurationNavigationItems(props);

  const {
    activePath,
    setActivePath,
    resetActivePath,
    next,
    nextDisabled,
    previous,
    previousDisabled,
  } = useSubconfigurationNavigation(items);

  const { NavigationComponents, AlignmentComponents, ContentComponents } =
    components;

  const Navigation = NavigationComponents[control];
  const AlignmentComponent = AlignmentComponents[orientation];

  const activeItem = items.find((item) => item.path === activePath);

  const { t } = useTranslation();

  return (
    <SubconfigurationNavigationApiProvider
      next={next}
      previous={previous}
      nextDisabled={nextDisabled}
      previousDisabled={previousDisabled}
    >
      <AlignmentComponent
        stickyNavigation={enableStickyNavigation}
        navigation={
          <Navigation
            orientation={orientation}
            activePath={activePath}
            setActivePath={setActivePath}
            items={items}
            navigationStyle={props.navigationStyle}
            showCompletenessIndicator={props.showCompletenessIndicator}
            stepperProps={props.stepperProps}
            textMode={textMode}
          />
        }
        content={
          activeItem ? (
            <Box
              display={"flex"}
              flexDirection={"column"}
              height="100%"
              gap={2}
            >
              <Box overflow="auto">
                <SubconfigurationNavigationContent
                  key={activeItem.path} // key needs to be set to force re-render
                  item={activeItem}
                  components={ContentComponents}
                />
              </Box>
              <Box
                display={"flex"}
                justifyContent={"space-between"}
                gap={1}
                marginTop="auto"
              >
                <Box display={"flex"} gap={1}>
                  <CustomSubconfigurationPreviousButton
                    onPreviousClicked={previous}
                    previousDisabled={previousDisabled}
                  />
                  <CustomSubconfigurationNextButton
                    onNextClicked={next}
                    nextDisabled={nextDisabled}
                  />
                </Box>
                <CustomSubconfigurationResetButton
                  onResetClicked={resetActivePath}
                  label={t("regiolux:reset.subConfiguration")}
                />
              </Box>
            </Box>
          ) : null
        }
      />
    </SubconfigurationNavigationApiProvider>
  );
}
